import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import useMaster from "hook/useMaster";
import useStaticMaster from "hook/useStaticMaster";
import { flattenObject, showToast } from "utils/utility";
import { useFlightCommissionData } from "libs/data-access";
import { IFlightCommissionResponse } from "libs/data-access/types";

import { IFlightCommissionB2B } from "../types";

interface IProps {
    setIsSaving: Dispatch<SetStateAction<boolean>>;
    selectedService: string | null;
};

export default function useFlightCommission({
    setIsSaving,
    selectedService
}: IProps) {

    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();

    // used for editing purpose
    const commission = location.state?.commission;
    const commissionId = searchParams.get("id");
    const service = searchParams.get("service");


    const {
        postFlightCommissionData,
        putFlightCommissionData
    } = useFlightCommissionData();
    const {
        getSearchType,
        searchTypeDropdown,
        isLoadingSearchType,
        getTravelType,
        travelTypeDropdown,
        isLoadingTravelType,
        getCommissionFormCharges,
        commissionFormChargesDropdown,
        isLoadingCommissionFormCharges
    } = useStaticMaster();
    const {
        groupTypes,
        getAllGroupTypes,
        flightAllClassTypes,
        getFlightAllClassTypes,
        flightCabinClassTypes,
        getFlightCabinClassTypes,
        supplierList,
        getSupplierList,
        airlinesList,
        getAirlinesBySuppliers,
        allCountryList,
        getAllCountryList,
        airportByCoutries,
        getAllAirportByCountries,
        airportByDestinationCoutries,
        getAllAirportByDestinationCountries
    } = useMaster();


    const [commissionDetails, setCommissionDetails] = useState<IFlightCommissionB2B>({
        details: {
            affiliateGroupIds: [],
            flightTypes: [],
            flightSearchTypes: [],
            flightOriginCountryIds: [],
            flightDestinationCountryIds: [],
            originAirportIds: [],
            destinationAirportIds: [],
            commissionChargeWithAmountTypes: [
                {
                    commissionFormCharge: "BASE_FARE",
                    amount: 0,
                    feeType: "PERCENTAGE"
                },
                {
                    commissionFormCharge: "SURCHARGE",
                    amount: 0,
                    feeType: "PERCENTAGE"
                },
                {
                    commissionFormCharge: "OTHER_TAXES",
                    amount: 0,
                    feeType: "PERCENTAGE"
                },
                {
                    commissionFormCharge: "INCENTIVE",
                    amount: 0,
                    feeType: "PERCENTAGE"
                },
                {
                    commissionFormCharge: "PLB_BASE_FARE",
                    amount: 0,
                    feeType: "PERCENTAGE"
                },
                {
                    commissionFormCharge: "PLB_YQ",
                    amount: 0,
                    feeType: "PERCENTAGE"
                },
                {
                    commissionFormCharge: "PLB_YR",
                    amount: 0,
                    feeType: "PERCENTAGE"
                },
                {
                    commissionFormCharge: "PLB_BASE_FARE_YQ",
                    amount: 0,
                    feeType: "PERCENTAGE"
                },
                {
                    commissionFormCharge: "PLB_BASE_FARE_YR",
                    amount: 0,
                    feeType: "PERCENTAGE"
                },
                {
                    commissionFormCharge: "CASHBACK",
                    amount: 0,
                    feeType: "PERCENTAGE"
                }
            ],
            flightTicketSupplierIds: [],
            flightAirlineIds: [],
            startDate: null,
            endDate: null,
            flightClass: [],
            flightCabin: []
        },
        rules: {
            affiliateGroupIds: "YES",
            flightTypes: "YES",
            flightSearchTypes: "NO",
            flightOriginCountryIds: "NO",
            flightDestinationCountryIds: "NO",
            originAirportIds: "NO",
            destinationAirportIds: "NO",
            commissionChargeWithAmountTypes: "NO",
            flightTicketSupplierIds: "YES",
            flightAirlineIds: "YES",
            startDate: "YES",
            endDate: "YES",
            flightClass: "NO",
            flightCabin: "NO"
        },
        errors: {
            affiliateGroupIds: undefined,
            flightTypes: undefined,
            flightSearchTypes: undefined,
            flightOriginCountryIds: undefined,
            flightDestinationCountryIds: undefined,
            originAirportIds: undefined,
            destinationAirportIds: undefined,
            commissionChargeWithAmountTypes: undefined,
            flightTicketSupplierIds: undefined,
            flightAirlineIds: undefined,
            startDate: undefined,
            endDate: undefined,
            flightClass: undefined,
            flightCabin: undefined
        }
    });


    useEffect(() => {
        getAllGroupTypes();
        // getMarkupTypes();
        // getSearchTypes();
        getFlightAllClassTypes();
        getFlightCabinClassTypes();
        selectedService && getSupplierList(selectedService as "FLIGHT" | "HOTEL" | "BUS");
        getAllCountryList();

        getSearchType();
        getTravelType();
        getCommissionFormCharges();
    }, []);

    useEffect(() => {
        if (commission?.id && service && commissionId) {
            mapCommissionDetailsToEdit(commission);
            // console.log('commission: ', commission);
            // console.log("Service: ", service, "ID: ", commissionId);
        };
    }, [service, commissionId, commission]);


    // HANDLER FUNCTIONS
    const handleSubmit = () => {
        if (commission?.id && service && commissionId) {
            updateCommission();
        } else {
            saveCommission();
        };
    };

    const mapCommissionDetailsToEdit = (commission: IFlightCommissionResponse) => {
        getAirlinesBySuppliers(commission.ticketSupplier?.map(each => String(each.id)));
        getAllAirportByCountries(commission.originCountry?.map(each => String(each.id)));
        getAllAirportByDestinationCountries(commission.destinationCountry?.map(each => String(each.id)));

        const mappedCommissionDetails: IFlightCommissionB2B["details"] = {
            affiliateGroupIds: commission.affiliateGroup.map(each => each.id),
            flightTypes: commission.flightTypes.map(each => String(each.value)),
            flightSearchTypes: commission.flightSearchTypes.map(each => String(each.value)),
            flightOriginCountryIds: commission.originCountry.map(each => each.id),
            flightDestinationCountryIds: commission.destinationCountry.map(each => each.id),
            originAirportIds: commission.originAirport.map(each => each.id),
            destinationAirportIds: commission.destinationAirport.map(each => each.id),
            commissionChargeWithAmountTypes: commission.commissionChargeWithAmountTypes.map(each => ({
                commissionFormCharge: each.commissionFormCharge.value,
                amount: each.amount,
                feeType: each.feeType
            })),
            flightTicketSupplierIds: commission.ticketSupplier.map(each => each.id),
            flightAirlineIds: commission.flightAirline.map(each => each.id),
            startDate: commission.startDate,
            endDate: commission.endDate,
            flightClass: commission.flightClass.map(each => String(each.value)),
            flightCabin: commission.flightCabin.map(each => String(each.value))
        };

        setCommissionDetails(prev => ({
            ...prev,
            details: mappedCommissionDetails
        }));
    };

    const updateCommissionFormCharge = (
        commissionFormCharge: string,
        action: "CHARGE" | "FEE_TYPE",
        value: number | string
    ) => {
        setCommissionDetails(prev => ({
            ...prev,
            details: {
                ...prev.details,
                commissionChargeWithAmountTypes: prev.details.commissionChargeWithAmountTypes.map(each => {
                    if (each.commissionFormCharge === commissionFormCharge) {
                        let charge = each;
                        if (action === "CHARGE") {
                            charge.amount = value as number;
                        } else {
                            charge.feeType = value as "FIXED" | "PERCENTAGE";
                        };

                        return charge;
                    };
                    return each;
                })
            }
        }));
    };

    const validateCommission = () => {
        const errors: IFlightCommissionB2B["errors"] = {
            affiliateGroupIds: (
                !commissionDetails.details.affiliateGroupIds.length
                &&
                commissionDetails.rules.affiliateGroupIds === "YES"
            ) ? "Affiliate group is mandatory" : undefined,
            flightTypes: (
                !commissionDetails.details.flightTypes.length
                &&
                commissionDetails.rules.flightTypes === "YES"
            ) ? "Flight type is mandatory" : undefined,
            flightSearchTypes: (
                !commissionDetails.details.flightSearchTypes.length
                &&
                commissionDetails.rules.flightSearchTypes === "YES"
            ) ? "Flight search type is mandatory" : undefined,
            flightOriginCountryIds: (
                !commissionDetails.details.flightOriginCountryIds.length
                &&
                commissionDetails.rules.flightOriginCountryIds === "YES"
            ) ? "Flight origin country is mandatory" : undefined,
            flightDestinationCountryIds: (
                !commissionDetails.details.flightDestinationCountryIds.length
                &&
                commissionDetails.rules.flightDestinationCountryIds === "YES"
            ) ? "Flight destination country is mandatory" : undefined,
            originAirportIds: (
                !commissionDetails.details.originAirportIds.length
                &&
                commissionDetails.rules.originAirportIds === "YES"
            ) ? "Flight origin airport is mandatory" : undefined,
            destinationAirportIds: (
                !commissionDetails.details.destinationAirportIds.length
                &&
                commissionDetails.rules.destinationAirportIds === "YES"
            ) ? "Flight destination airport is mandatory" : undefined,
            commissionChargeWithAmountTypes: (
                !commissionDetails.details.commissionChargeWithAmountTypes.length
                &&
                commissionDetails.rules.commissionChargeWithAmountTypes === "YES"
            ) ? "Commission charge with amount is mandatory" : undefined,
            flightTicketSupplierIds: (
                !commissionDetails.details.flightTicketSupplierIds.length
                &&
                commissionDetails.rules.flightTicketSupplierIds === "YES"
            ) ? "Flight ticket supplier is mandatory" : undefined,
            flightAirlineIds: (
                !commissionDetails.details.flightAirlineIds.length
                &&
                commissionDetails.rules.flightAirlineIds === "YES"
            ) ? "Flight airline is mandatory" : undefined,
            startDate: (
                !commissionDetails.details.startDate
                &&
                commissionDetails.rules.startDate === "YES"
            ) ? "Start date is mandatory" : undefined,
            endDate: (
                !commissionDetails.details.endDate
                &&
                commissionDetails.rules.endDate === "YES"
            ) ? "End date is mandatory" : undefined,
            flightClass: (
                !commissionDetails.details.flightClass.length
                &&
                commissionDetails.rules.flightClass === "YES"
            ) ? "Flight class is mandatory" : undefined,
            flightCabin: (
                !commissionDetails.details.flightCabin.length
                &&
                commissionDetails.rules.flightCabin === "YES"
            ) ? "Flight cabin is mandatory" : undefined
        };

        const isCommissionChargeWithAmountTypesValid = commissionDetails.details.commissionChargeWithAmountTypes.some(each => {
            if (each.amount === 0) {
                // errors.commissionChargeWithAmountTypes = "Commission charge is mandatory";
                return false;
            };
            return true;
        });

        if (!isCommissionChargeWithAmountTypesValid) {
            errors.commissionChargeWithAmountTypes = "Commission charge is mandatory";
        };

        setCommissionDetails(prev => ({
            ...prev,
            errors: errors
        }));

        let isValid = Object.values(flattenObject(errors)).every(val => !val);

        // console.log("errors: ", errors);
        // console.log("isValid: ", isValid);

        return isValid;
    };


    // API CALLS
    const saveCommission = async () => {
        const isValid = validateCommission();
        if (!isValid) return;

        console.log("commissionDetails: ", commissionDetails.details);

        setIsSaving(true);

        const response = await postFlightCommissionData(``, commissionDetails.details);

        if (response.isSuccess) {
            // console.log("Response for saving commission: ", response);
            showToast({
                type: "success",
                message: "Commission saved successfully!",
            });
            navigate(-1);
        } else {
            console.log("Error while saving commission: ", response);
            showToast({
                type: "error",
                message: response.message || "Error while saving commission!",
            });
        };

        setIsSaving(false);
    };

    const updateCommission = async () => {
        const isValid = validateCommission();
        if (!isValid) return;

        // console.log("commissionDetails: ", commissionDetails.details);

        setIsSaving(true);

        const response = await putFlightCommissionData(`/${commissionId}`, commissionDetails.details);

        if (response.isSuccess) {
            // console.log("Response for saving commission: ", response);
            showToast({
                type: "success",
                message: "Commission updated successfully!",
            });
            navigate(-1);
        } else {
            console.log("Error while updating commission: ", response);
            showToast({
                type: "error",
                message: response.message || "Error while updating commission!",
            });
        };

        setIsSaving(false);
    };


    return {
        searchTypeDropdown,
        travelTypeDropdown,
        commissionFormChargesDropdown,
        groupTypes,
        flightAllClassTypes,
        flightCabinClassTypes,
        supplierList,
        airlinesList,
        getAirlinesBySuppliers,
        allCountryList,
        airportByCoutries,
        getAllAirportByCountries,
        airportByDestinationCoutries,
        getAllAirportByDestinationCountries,
        commissionDetails,
        setCommissionDetails,
        handleSubmit,
        updateCommissionFormCharge,
    };
};